import * as React from "react";
import {Theme} from "@vebox/etui-themes";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {
    Actions,
    ActionsEnum,
    CensusEquipmentStatusDetails,
    CensusEquipmentStatusDetailsEnum,
    CensusEquipmentStatuses,
    CensusEquipmentStatusesEnum,
    CensusEquipmentStatusReasons
} from "../../../processes/massive_actions/CensusCooler/data/models/CensusEnums";
import {
    MappedDetailCount
} from "../../../processes/massive_actions/CensusCooler/data/models/JSONCensusSuggestedActionsAnalysisSummary";
import {Section} from "../../../processes/massive_actions/CensusCooler/pages/CensusStatusPage/types";
import {useTheme} from "@vebox/etui-system";
import {useCensusEnums} from "../../../processes/massive_actions/CensusCooler/data/hooks/CecnsusCoolerControllerHooks";
import {DesAccordion, DesAccordionSection} from "../../../components/DesAccordion";
import {getHeaderDetailsHeader} from "../../../processes/massive_actions/CensusCooler/pages/CensusStatusPage/utils";
import {SuggestedAndFinalActionAccordionHeader} from "../SuggestedAndFinalActionAccordionHeader";
import {accordionStyle} from "../../../processes/massive_actions/CensusCooler/pages/CensusStatusPage";
import {FinalActionsTable} from "../FinalActionsTable";
import {AccordionContextProps} from "../../../components/DesAccordion/context";

declare var AppTranslations: AppTranslations;

interface FinalEquipmentWithStatusSectionProps {
    equipmentCount: number;
    tableHeaderName: string;
    status: CensusEquipmentStatusesEnum;
    statusDetail?: CensusEquipmentStatusDetailsEnum;
    inventoryStatusReason?: CensusEquipmentStatusReasons;
    detailCount?: MappedDetailCount[];
    translatedActionsEnum: Actions;
    suggestedAction?: ActionsEnum;
    finalAction?: ActionsEnum;
    level: number;
    height?: string;
    index?: number;
    toggleSection?: (section: Section) => void;
    descriptions: CensusEquipmentStatuses & CensusEquipmentStatusDetails & CensusEquipmentStatusReasons;
    toggleDrawer: () => void;
    previousOpenedSections: AccordionContextProps['openSections'][]
}

export const FinalEquipmentWithStatusSection: React.FC<
    FinalEquipmentWithStatusSectionProps
> = ({
         equipmentCount,
         tableHeaderName,
         status,
         statusDetail,
         inventoryStatusReason,
         detailCount,
         translatedActionsEnum,
         suggestedAction,
         finalAction,
         level,
         height,
         index,
         toggleSection,
         descriptions,
         toggleDrawer,
         previousOpenedSections,
         ...rest
     }) => {
    const theme = useTheme();
    const {data} = useCensusEnums(true);

    const ref = React.useRef<HTMLDivElement>(null);

    const onClick = (level: number) => () => {
        toggleSection({name: tableHeaderName, level: level});
    };

    const getDescriptions = () => {
        if (status && !statusDetail && !inventoryStatusReason) {
            return descriptions[status];
        }
        if (statusDetail && level === 0) {
            return `${descriptions[statusDetail]} 
                ${inventoryStatusReason
                ? descriptions[inventoryStatusReason]
                : ""}`;
        }
        if (level === 1) {
            return `${inventoryStatusReason && inventoryStatusReason !== CensusEquipmentStatusReasons.OTHER
                ? descriptions[inventoryStatusReason]
                : AppTranslations.massiveActions.censusCooler.otherDescription}`;
        }
        return ""
    }


    const renderChildren = () => {
        if (detailCount?.length > 0) {
            return (
                <DesAccordion
                    enableSingleSectionOpen={true}
                    initialState={previousOpenedSections[1]}
                >
                    {detailCount.map((detail, index) => {
                        return (
                            <FinalEquipmentWithStatusSection
                                key={index}
                                equipmentCount={detail.count}
                                tableHeaderName={getHeaderDetailsHeader(detail, data)}
                                inventoryStatusReason={detail?.inventoryStatusReason}
                                status={status}
                                statusDetail={statusDetail}
                                suggestedAction={detail?.suggestedAction}
                                level={1}
                                height={`calc(${height} - 52px)`}
                                toggleSection={toggleSection}
                                translatedActionsEnum={translatedActionsEnum}
                                descriptions={descriptions}
                                toggleDrawer={toggleDrawer}
                                previousOpenedSections={previousOpenedSections}
                                {...rest}
                            />
                        );
                    })}
                </DesAccordion>
            );
        } else {
            return (
                <FinalActionsTable
                    status={status}
                    statusDetail={statusDetail}
                    statusReason={inventoryStatusReason}
                    translatedActionsEnum={translatedActionsEnum}
                    height={`calc(${height} - 80px)`}
                    toggleDrawer={toggleDrawer}
                />
            );
        }
    };

    const accordionChildren = React.useMemo(
        () => renderChildren(),
        [
            detailCount,
            inventoryStatusReason,
            status,
            statusDetail,
            data,
            translatedActionsEnum
        ],
    );

    const renderHeader = (isOpen: boolean) => {
        return <SuggestedAndFinalActionAccordionHeader
            statusIcon={status}
            headerName={tableHeaderName}
            equipmentCount={equipmentCount}
            isOpen={isOpen}
            translatedActionsEnum={translatedActionsEnum}
            onClick={onClick(level)}
            status={status}
            description={getDescriptions()}
            level={level}
        />
    }

    return (
        <DesAccordionSection
            key={tableHeaderName}
            title={tableHeaderName + " (" + equipmentCount + ")"}
            id={tableHeaderName}
            keepSectionContentInDOM={false}
            styles={{
                ...accordionStyle(
                    theme as Theme,
                    (detailCount ?? [])?.length > 0,
                    level,
                    `calc(${height} - 80px)`,
                ),
            }}
            renderHeader={renderHeader}
            containerRef={ref}
        >
            {accordionChildren}
        </DesAccordionSection>
    );
};
